import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { fetchDashboard, fetchRecords } from "../api/client";
import { MonthCalendar } from "../components/MonthCalendar";
import { AppointmentIcon, ChevronRightIcon, ExerciseIcon, PlusIcon, VitalsIcon, WeightIcon } from "../components/Icons";
import type { ReactNode } from "react";
import { classifyBloodPressure, escBloodPressureReference, type BloodPressureCategory } from "../lib/bloodPressure";
import { buildDashboardInsights } from "../lib/dashboardInsights";
import { formatMeasurement, localDateKey } from "../lib/records";
import { recordVisuals } from "../lib/recordVisuals";
import type { HealthRecord, RecordKind } from "../types";
import { usePreferences } from "../preferences/usePreferences";
import type { BloodPressureThresholds } from "../preferences/types";
import { displayWeight, weightUnitLabel } from "../lib/weightUnits";
import { StatusToast } from "../components/StatusToast";
import { TherapyNotice } from "../therapy/TherapyNotice";

const RecordModal = lazy(() => import("../components/RecordModal").then((module) => ({ default: module.RecordModal })));
const DayEntriesModal = lazy(() => import("../components/DayEntriesModal").then((module) => ({ default: module.DayEntriesModal })));

interface RecordDialogState {
  record?: HealthRecord;
  initialDate?: string;
  initialKind?: RecordKind;
  returnDate?: string;
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const preferences = usePreferences();
  const [recordDialog, setRecordDialog] = useState<RecordDialogState>();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [visibleMonth, setVisibleMonth] = useState<Date>();
  const [statusMessage, setStatusMessage] = useState<string>();
  const dismissStatus = useCallback(() => setStatusMessage(undefined), []);
  const dashboard = useQuery({ queryKey: ["dashboard"], queryFn: fetchDashboard });
  const records = useQuery({ queryKey: ["records", "all"], queryFn: () => fetchRecords() });

  if (dashboard.isPending || records.isPending) return <PageLoading label={t("dashboard.loading")} />;
  if (dashboard.isError || records.isError) return <PageError onRetry={() => void Promise.all([dashboard.refetch(), records.refetch()])} />;

  const { summary } = dashboard.data;
  const now = new Date(dashboard.data.now);
  const insights = buildDashboardInsights(records.data, now);
  const month = visibleMonth ?? new Date(now.getFullYear(), now.getMonth(), 1);
  const selectedRecords = selectedDate
    ? records.data.filter((record) => localDateKey(new Date(record.recordedAt)) === selectedDate)
    : [];

  function openRecordForDate(date: string) {
    setSelectedDate(undefined);
    setRecordDialog({ initialDate: date, initialKind: date > localDateKey(now) ? "appointment" : "weight", returnDate: date });
  }

  function editRecord(record: HealthRecord) {
    setSelectedDate(undefined);
    setRecordDialog({ record, returnDate: localDateKey(new Date(record.recordedAt)) });
  }

  function closeRecordDialog() {
    const returnDate = recordDialog?.returnDate;
    setRecordDialog(undefined);
    if (returnDate) setSelectedDate(returnDate);
  }

  function handleRecordSaved() {
    const editing = Boolean(recordDialog?.record);
    closeRecordDialog();
    setStatusMessage(t(editing ? "recordForm.updated" : "recordForm.saved"));
  }

  return (
    <div className="mx-auto max-w-[92rem]">
      <header className="flex flex-col gap-5 border-b border-line pb-7 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="page-eyebrow">
            {new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "en", { weekday: "long", day: "numeric", month: "long" }).format(now)}
          </p>
          <h1 className="page-title max-w-3xl">{t("dashboard.healthOverview")}</h1>
          <p className="page-description mt-3">{t("dashboard.overviewDescription")}</p>
        </div>
        <button type="button" onClick={() => setRecordDialog({})} className="pressable inline-flex min-h-11 shrink-0 items-center justify-center gap-2 self-start whitespace-nowrap rounded-xl bg-teal-700 px-5 py-2.5 text-sm font-bold text-canvas shadow-[0_0.65rem_1.8rem_color-mix(in_oklch,var(--color-teal-700)_22%,transparent)] hover:bg-teal-800 focus-ring sm:self-auto">
          <PlusIcon className="size-4" />
          {t("actions.logRecord")}
        </button>
      </header>

      <TherapyNotice />
      <section aria-labelledby="latest-heading" className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 id="latest-heading" className="text-sm font-bold uppercase tracking-[0.14em] text-muted">{t("dashboard.latestReadings")}</h2>
          <span className="text-xs font-semibold text-muted">{t("dashboard.totalRecords", { count: summary.totalRecords })}</span>
        </div>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[1.4rem] border border-line bg-line xl:grid-cols-4">
          <SummaryCard
            label={t("dashboard.weight")}
            value={summary.latestWeight === undefined ? "—" : formatMeasurement(displayWeight(summary.latestWeight, preferences.weightUnit), i18n.resolvedLanguage ?? "en")}
            unit={weightUnitLabel(preferences.weightUnit)}
            icon={<WeightIcon className="size-[18px]" />}
            badgeClass={recordVisuals.weight.iconClass}
            insight={weightInsight(insights.weight.latest?.value, insights.weight.previous?.value, preferences.weightUnit, i18n.resolvedLanguage ?? "en", t)}
            to="/trends#weight-trend"
            linkLabel={t("dashboard.viewTrend", { kind: t("dashboard.weight") })}
            linkText={t("dashboard.openTrend")}
          />
          <SummaryCard
            label={t("dashboard.bloodPressure")}
            value={summary.latestSystolic === undefined || summary.latestDiastolic === undefined ? "—" : `${summary.latestSystolic}/${summary.latestDiastolic}`}
            unit="mmHg"
            icon={<VitalsIcon className="size-[18px]" />}
            badgeClass={recordVisuals.vitals.iconClass}
            insight={pressureInsight(insights.vitals.latest, insights.vitals.previous, t)}
            status={insights.vitals.latest ? bloodPressureStatus(insights.vitals.latest, preferences.bloodPressureThresholds, t) : undefined}
            to="/trends#vitals-trend"
            linkLabel={t("dashboard.viewTrend", { kind: t("dashboard.bloodPressure") })}
            linkText={t("dashboard.openTrend")}
          />
          <SummaryCard
            label={t("dashboard.exerciseThisWeek")}
            value={String(summary.exerciseMinutes)}
            unit={t("dashboard.minutes", { count: summary.exerciseMinutes })}
            icon={<ExerciseIcon className="size-[18px]" />}
            badgeClass={recordVisuals.exercise.iconClass}
            insight={exerciseInsight(insights.exercise.currentMinutes, insights.exercise.previousMinutes, t)}
            to="/trends#exercise-trend"
            linkLabel={t("dashboard.viewTrend", { kind: t("dashboard.exerciseThisWeek") })}
            linkText={t("dashboard.openTrend")}
          />
          <SummaryCard
            label={t("dashboard.upcomingVisits")}
            value={String(summary.upcomingAppointments)}
            unit={t("dashboard.appointments", { count: summary.upcomingAppointments })}
            icon={<AppointmentIcon className="size-[18px]" />}
            badgeClass={recordVisuals.appointment.iconClass}
            insight={insights.nextAppointment ? t("dashboard.nextAppointment", { date: new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "en", { dateStyle: "medium" }).format(new Date(insights.nextAppointment.recordedAt)) }) : t("dashboard.noUpcomingAppointments")}
            to="/history?kind=appointment"
            linkLabel={t("dashboard.viewHistory", { kind: t("dashboard.upcomingVisits") })}
            linkText={t("dashboard.openHistory")}
          />
        </div>
        <p className="mt-3 text-xs leading-relaxed text-muted">
          {t("dashboard.bloodPressureDisclaimer")} {" "}
          <a className="font-bold text-teal-700 underline decoration-line-strong underline-offset-4 hover:text-teal-800 focus-ring" href={escBloodPressureReference.sourceUrl} target="_blank" rel="noreferrer">{t("dashboard.bloodPressureSource")}</a>
        </p>
      </section>

      <div className="mt-9">
        <MonthCalendar month={month} now={now} records={records.data} selectedDate={selectedDate} onMonthChange={setVisibleMonth} onSelectDate={setSelectedDate} />
      </div>
      {recordDialog ? (
        <Suspense fallback={null}>
          <RecordModal
            open
            initialDate={recordDialog.initialDate}
            initialKind={recordDialog.initialKind}
            record={recordDialog.record}
            onClose={closeRecordDialog}
            onSaved={handleRecordSaved}
          />
        </Suspense>
      ) : null}
      {selectedDate ? (
        <Suspense fallback={null}>
          <DayEntriesModal
            date={selectedDate}
            records={selectedRecords}
            onClose={() => setSelectedDate(undefined)}
            onAddRecord={() => openRecordForDate(selectedDate)}
            onEditRecord={editRecord}
          />
        </Suspense>
      ) : null}
      <StatusToast message={statusMessage} onDismiss={dismissStatus} />
    </div>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  unit: string;
  icon: ReactNode;
  badgeClass: string;
  insight?: string;
  status?: { label: string; className: string };
  to: string;
  linkLabel: string;
  linkText: string;
}

function SummaryCard({ label, value, unit, icon, badgeClass, insight, status, to, linkLabel, linkText }: SummaryCardProps) {
  return (
    <Link to={to} aria-label={linkLabel} className="group flex min-w-0 flex-col bg-surface p-4 transition-colors duration-200 hover:bg-surface-raised focus-ring sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="min-h-8 text-xs font-bold uppercase tracking-[0.13em] text-muted">{label}</p>
          <p className="mt-4 flex items-baseline gap-x-2 whitespace-nowrap tabular-nums">
            <strong className="text-2xl font-semibold tracking-[-0.035em] sm:text-3xl">{value}</strong>
            <span className="text-xs font-semibold text-muted">{unit}</span>
          </p>
        </div>
        <span className={`hidden size-9 shrink-0 place-items-center rounded-xl sm:grid ${badgeClass}`}>{icon}</span>
      </div>
      {status ? <span className={`mt-3 w-fit rounded-full px-2.5 py-1 text-[0.68rem] font-bold ${status.className}`}>{status.label}</span> : null}
      <p className="mt-4 min-h-8 text-[0.7rem] font-semibold leading-4 text-muted sm:text-xs">{insight}</p>
      <span className="mt-auto inline-flex items-center gap-1 pt-4 text-xs font-bold text-teal-700 group-hover:text-teal-800">
        {linkText}
        <ChevronRightIcon className="size-4" />
      </span>
    </Link>
  );
}

function weightInsight(latest: number | undefined, previous: number | undefined, unit: "kg" | "lb", locale: string, t: ReturnType<typeof useTranslation>["t"]): string {
  if (latest === undefined || previous === undefined) return t("dashboard.addReadingForComparison");
  const delta = displayWeight(latest - previous, unit);
  return t("dashboard.weightChange", { value: signedNumber(delta, locale), unit: weightUnitLabel(unit) });
}

function pressureInsight(latest: HealthRecord | undefined, previous: HealthRecord | undefined, t: ReturnType<typeof useTranslation>["t"]): string {
  if (latest?.systolic === undefined || previous?.systolic === undefined) return t("dashboard.addReadingForComparison");
  return t("dashboard.pressureChange", { value: signedInteger(latest.systolic - previous.systolic) });
}

function exerciseInsight(current: number, previous: number, t: ReturnType<typeof useTranslation>["t"]): string {
  return t("dashboard.exerciseChange", { value: signedInteger(current - previous) });
}

function bloodPressureStatus(record: HealthRecord, thresholds: BloodPressureThresholds, t: ReturnType<typeof useTranslation>["t"]): { label: string; className: string } | undefined {
  if (record.systolic === undefined || record.diastolic === undefined) return undefined;
  const category = classifyBloodPressure(record.systolic, record.diastolic, thresholds);
  return { label: t(`dashboard.bloodPressureCategories.${category}`), className: bloodPressureCategoryClass(category) };
}

function bloodPressureCategoryClass(category: BloodPressureCategory): string {
  if (category === "nonElevated") return "bg-exercise-soft text-exercise-strong";
  if (category === "elevated") return "bg-appointment-soft text-appointment-strong";
  return "bg-vitals-soft text-vitals-strong";
}

function signedInteger(value: number): string {
  return `${value > 0 ? "+" : ""}${value}`;
}

function signedNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { signDisplay: "exceptZero", maximumFractionDigits: 1 }).format(value);
}

export function PageLoading({ label }: { label: string }) {
  return (
    <div className="mx-auto max-w-6xl" role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label}</span>
      <div className="border-b border-line pb-7">
        <div className="skeleton-block h-3 w-32" />
        <div className="skeleton-block mt-4 h-12 w-full max-w-xl" />
        <div className="skeleton-block mt-4 h-4 w-full max-w-md" />
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="surface-elevated rounded-2xl p-5">
            <div className="skeleton-block h-3 w-24" />
            <div className="skeleton-block mt-6 h-8 w-28" />
          </div>
        ))}
      </div>
      <div className="surface-elevated mt-7 rounded-[1.4rem] p-6">
        <div className="skeleton-block h-7 w-44" />
        <div className="skeleton-block mt-6 h-64 w-full" />
      </div>
    </div>
  );
}

export function PageError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="surface-elevated mx-auto mt-16 max-w-xl rounded-[1.4rem] border-pulse/30 bg-pulse-soft px-6 py-8 text-center">
      <h1 className="text-2xl font-semibold text-ink">{t("errors.title")}</h1>
      <p className="mt-2 text-sm leading-6 text-muted">{t("errors.message")}</p>
      <button type="button" onClick={onRetry} className="pressable mt-5 rounded-xl bg-teal-700 px-5 py-2.5 text-sm font-bold text-canvas focus-ring">{t("actions.tryAgain")}</button>
    </div>
  );
}
